import { useCallback, useEffect, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { AlertTriangle, BarChart3, Loader2, Sparkles } from "lucide-react";
import { api, apiError, inr, logError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatusLegend, StatusPill, STATUS_COLOUR } from "@/components/StockStatus";
import { toast } from "sonner";

const RANGES = [["7", "7 days"], ["30", "30 days"], ["90", "90 days"], ["365", "1 year"]];
const AXIS = { fontSize: 10 };
const CAT_COLOURS = ["#0f3d2e", "#c9a227", "#1d4ed8", "#b45309", "#0e7490", "#7c3aed", "#64748b"];
const LEVEL_TONE = { critical: "destructive", warning: "secondary", info: "outline" };

function Card({ label, value, hint, testId }) {
  return (
    <div className="surface p-4" data-testid={testId}>
      <div className="overline">{label}</div>
      <div className="mt-1 font-display text-xl font-semibold sm:text-2xl">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function ItemList({ rows, testId, empty, extra }) {
  if (!rows?.length) return <p className="text-xs text-muted-foreground">{empty}</p>;
  return (
    <div data-testid={testId}>
      {rows.map((r) => (
        <div key={`${r.id}-${r.name}`}
          className="flex items-center justify-between gap-2 border-b border-border py-1.5 text-xs last:border-0">
          <span className="truncate">{r.name}</span>
          <span className="flex shrink-0 items-center gap-2">
            <span className="font-mono-num text-muted-foreground">{extra(r)}</span>
            {r.status && <StatusPill status={r.status} />}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function InventoryDashboard({ onOpenItem }) {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null);
  const [insight, setInsight] = useState(null);
  const [thinking, setThinking] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, planRes] = await Promise.all([
        api.get(`/inventory/dashboard?days=${days}`),
        api.get(`/inventory/purchase-plan?days=${Math.max(Number(days), 90)}`).catch(() => null),
      ]);
      setData(d);
      setPlan(planRes?.data || null);
    } catch (e) {
      logError("inventory dashboard", e);
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const askAi = async () => {
    setThinking(true);
    try {
      const { data: r } = await api.post("/inventory/ai-insight", { days: 90 });
      setInsight(r);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setThinking(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const statusChart = Object.entries(data.status_count)
    .filter(([, c]) => c > 0)
    .map(([status, count]) => ({ status, count, label: data.status_meta[status].label }));
  const money = data.can_see_cost;

  return (
    <div className="space-y-4" data-testid="inventory-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <StatusLegend />
        <div className="flex gap-1" data-testid="dashboard-range-buttons">
          {RANGES.map(([v, label]) => (
            <Button key={v} size="sm" variant={days === v ? "default" : "outline"}
              className="h-7 px-2 text-xs" data-testid={`dashboard-range-${v}`}
              onClick={() => setDays(v)}>
              {label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {money && (
          <Card testId="kpi-inventory-value" label="Total inventory value"
            value={inr(data.totals.inventory_value)} hint="priced items only" />
        )}
        <Card testId="kpi-total-quantity" label="Total stock quantity"
          value={data.totals.quantity.toLocaleString("en-IN")}
          hint={`${data.totals.items} items in register`} />
        {money && (
          <Card testId="kpi-purchase-amount" label={`Purchase amount (${days}d)`}
            value={inr(data.totals.purchase_amount)} hint={`${data.totals.bills} bills recorded`} />
        )}
        <Card testId="kpi-consumption" label={`Consumption (${days}d)`}
          value={data.totals.consumption_quantity.toLocaleString("en-IN")} hint="issued / used" />
        <Card testId="kpi-stock-in-out" label={`Stock in / out (${days}d)`}
          value={`${data.totals.stock_in.toLocaleString("en-IN")} / ${data.totals.stock_out.toLocaleString("en-IN")}`}
          hint="quantity moved" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="surface p-4">
          <div className="overline mb-2">Stock status</div>
          <ResponsiveContainer width="100%" height={175} minHeight={175}>
            <PieChart>
              <Pie data={statusChart} dataKey="count" nameKey="label" innerRadius={40} outerRadius={68}>
                {statusChart.map((s) => (
                  <Cell key={s.status} fill={STATUS_COLOUR[s.status]} />
                ))}
              </Pie>
              <Tooltip formatter={(v, n) => [`${v} items`, n]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-2">
            {statusChart.map((s) => (
              <StatusPill key={s.status} status={s.status} label={`${s.label} (${s.count})`}
                testId={`status-count-${s.status}`} />
            ))}
          </div>
        </div>

        <div className="surface p-4" data-testid="chart-by-category">
          <div className="overline mb-2">Stock by category</div>
          <ResponsiveContainer width="100%" height={175} minHeight={175}>
            <BarChart data={data.by_category} margin={{ top: 4, right: 8, left: 12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" tick={AXIS} interval={0} angle={-15} height={42} />
              <YAxis tick={AXIS} />
              <Tooltip formatter={(v, n) => [n === "value" ? inr(v) : v, n]} />
              <Bar dataKey={money ? "value" : "quantity"} radius={[3, 3, 0, 0]}>
                {data.by_category.map((c, i) => (
                  <Cell key={c.name} fill={CAT_COLOURS[i % CAT_COLOURS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="surface p-4" data-testid="chart-by-warehouse">
          <div className="overline mb-2">Stock by warehouse</div>
          <ResponsiveContainer width="100%" height={175} minHeight={175}>
            <BarChart data={data.by_warehouse} margin={{ top: 4, right: 8, left: 12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" tick={AXIS} interval={0} angle={-15} height={42} />
              <YAxis tick={AXIS} />
              <Tooltip formatter={(v, n) => [n === "value" ? inr(v) : v, n]} />
              <Bar dataKey={money ? "value" : "quantity"} fill="#0f3d2e" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="surface p-4" data-testid="chart-trend">
          <div className="overline mb-2">Inventory trend (in / out)</div>
          {data.trend.length === 0 ? (
            <p className="text-xs text-muted-foreground">No movement in this period.</p>
          ) : (
            <ResponsiveContainer width="100%" height={175} minHeight={175}>
              <LineChart data={data.trend} margin={{ top: 4, right: 8, left: 12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tick={AXIS} />
                <YAxis tick={AXIS} />
                <Tooltip />
                <Line type="monotone" dataKey="in" stroke="#15803d" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="out" stroke="#dc2626" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="net" stroke="#c9a227" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="surface p-4" data-testid="smart-alerts">
        <div className="overline mb-2 flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-destructive" /> Smart alerts
        </div>
        {data.alerts.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nothing needs your attention right now.</p>
        ) : (
          data.alerts.map((a, i) => (
            <button key={`${a.kind}-${a.item_id}-${i}`} type="button"
              data-testid={`alert-${a.kind}-${i}`}
              onClick={() => a.item_id && onOpenItem?.(a.item_id)}
              className="flex w-full items-center justify-between gap-2 border-b border-border py-1.5 text-left text-xs last:border-0 hover:bg-secondary/50">
              <span>{a.message}</span>
              <Badge variant={LEVEL_TONE[a.level]} className="shrink-0 text-[9px] uppercase">
                {a.kind.replace(/_/g, " ")}
              </Badge>
            </button>
          ))
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="surface p-4">
          <div className="overline mb-2">Critical / out of stock</div>
          <ItemList rows={data.flagged.critical} testId="flag-critical"
            empty="Nothing critical." extra={(r) => r.primary} />
        </div>
        <div className="surface p-4">
          <div className="overline mb-2">Reorder required</div>
          <ItemList rows={data.flagged.reorder} testId="flag-reorder"
            empty="No item is at reorder level." extra={(r) => `${r.primary} • reorder ${r.reorder_level}`} />
        </div>
        <div className="surface p-4">
          <div className="overline mb-2">Excess / overstock</div>
          <ItemList rows={data.flagged.excess} testId="flag-excess"
            empty="No overstock." extra={(r) => `${r.primary} • max ${r.maximum_level}`} />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="surface p-4">
          <div className="overline mb-2">Fast moving</div>
          <ItemList rows={data.fast_moving} testId="fast-moving"
            empty="No consumption recorded." extra={(r) => `${r.qty} used`} />
        </div>
        <div className="surface p-4">
          <div className="overline mb-2">Slow / non-moving</div>
          <ItemList rows={data.slow_moving} testId="slow-moving"
            empty="Everything moved in this period." extra={(r) => `${r.quantity} ${r.unit || ""}`} />
        </div>
        <div className="surface p-4">
          <div className="overline mb-2">Frequently purchased</div>
          <ItemList rows={data.frequently_purchased} testId="frequently-purchased"
            empty="No purchases recorded." extra={(r) => `${r.times}×`} />
        </div>
      </div>

      {plan && (
        <div className="surface p-4" data-testid="purchase-plan">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="overline flex items-center gap-1.5">
              <BarChart3 className="h-3.5 w-3.5 text-primary" /> AI purchase plan
            </div>
            <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs"
              data-testid="ai-insight-button" onClick={askAi} disabled={thinking}>
              {thinking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              Written brief
            </Button>
          </div>
          <p className="mb-3 text-[11px] text-muted-foreground">
            {plan.basis} Average supplier lead time {plan.lead_days} days.
            {plan.can_see_cost && plan.estimated_buy_now_cost
              ? ` Estimated immediate purchase ${inr(plan.estimated_buy_now_cost)}.`
              : ""}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="purchase-plan-table">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-1.5 pr-2">Item</th>
                  <th className="py-1.5 pr-2">Stock</th>
                  <th className="hidden py-1.5 pr-2 sm:table-cell">Use / day</th>
                  <th className="hidden py-1.5 pr-2 sm:table-cell">Cover</th>
                  <th className="py-1.5 pr-2">Purchase</th>
                  <th className="py-1.5 pr-2">When</th>
                </tr>
              </thead>
              <tbody>
                {[...plan.buy_now, ...plan.plan_soon].slice(0, 25).map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0"
                    data-testid={`plan-row-${r.id}`}>
                    <td className="py-1.5 pr-2">
                      <button type="button" className="hover:underline"
                        onClick={() => onOpenItem?.(r.id)}>{r.name}</button>
                      <div className="mt-0.5"><StatusPill status={r.status} /></div>
                    </td>
                    <td className="py-1.5 pr-2 font-mono-num">{r.current_stock} {r.unit}</td>
                    <td className="hidden py-1.5 pr-2 font-mono-num sm:table-cell">{r.per_day_consumption}</td>
                    <td className="hidden py-1.5 pr-2 font-mono-num sm:table-cell">
                      {r.cover_days == null ? "—" : `${r.cover_days}d`}
                    </td>
                    <td className="py-1.5 pr-2 font-mono-num font-medium">
                      {r.suggested_quantity} {r.unit}
                      {r.estimated_cost ? (
                        <div className="text-[10px] text-muted-foreground">{inr(r.estimated_cost)}</div>
                      ) : null}
                    </td>
                    <td className="py-1.5 pr-2">{r.when}</td>
                  </tr>
                ))}
                {plan.buy_now.length + plan.plan_soon.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-muted-foreground">
                      Nothing needs purchasing based on current consumption and stock levels.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {plan.dead_stock.length > 0 && (
            <p className="mt-3 text-[11px] text-muted-foreground" data-testid="dead-stock-note">
              Dead stock: {plan.dead_stock.map((d) => d.name).join(", ")}
            </p>
          )}
          {insight && (
            <div className="mt-3 rounded border border-border bg-secondary/40 p-3" data-testid="ai-insight">
              {insight.lines.map((l) => (
                <p key={l} className="py-0.5 text-sm">{l}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
